import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { formatMoney } from "@/lib/money";
import { partnerWorkspaceApi } from "./partnerWorkspaceApi";
import { CalendarDays, MapPin, Wallet, MessageSquare, Users, Camera } from "lucide-react";
import "./partnerWorkspace.css";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function todayLong() {
  return new Date().toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long" });
}

function firstName(name) {
  return (name || "").trim().split(/\s+/)[0] || "there";
}

// Status chips read the project's REAL Notion status — this heuristic just picks a tone
// (color) for whatever string comes back, it never invents a parallel "pipeline" the backend
// doesn't have. Unmapped/unknown statuses fall back to the neutral "muted" tone.
function statusTone(status) {
  const s = (status || "").toLowerCase();
  if (!s) return "muted";
  if (["won", "confirmed", "delivered", "completed", "closed", "paid"].some((k) => s.includes(k))) return "confirmed";
  if (["lost", "cancelled", "archived", "on hold"].some((k) => s.includes(k))) return "muted";
  if (["proposed", "discovery", "pitch", "quote"].some((k) => s.includes(k))) return "awaiting";
  return "progress";
}
const TONE_STYLE = {
  confirmed: { background: "var(--pw-success-bg)", color: "var(--pw-success-text)" },
  awaiting: { background: "#EEF1F4", color: "var(--pw-info-text)" },
  progress: { background: "#FFF3D6", color: "#8A6A12" },
  muted: { background: "#EFEEEA", color: "#7C7A72" },
};

function formatDateRange(startIso, endIso) {
  if (!startIso) return null;
  const opts = { day: "numeric", month: "short" };
  const start = new Date(startIso).toLocaleDateString("en-GB", opts);
  if (!endIso || endIso === startIso) return start;
  return `${start} – ${new Date(endIso).toLocaleDateString("en-GB", opts)}`;
}

function ShootDateTile({ iso }) {
  const d = new Date(iso);
  return (
    <div className="h-[52px] w-[52px] rounded-xl flex flex-col items-center justify-center shrink-0" style={{ background: "var(--pw-warm-tint)", border: "1px solid var(--pw-border)" }}>
      <div className="pw-display text-[18px] leading-none">{d.getDate()}</div>
      <div className="text-[9.5px] font-bold uppercase tracking-wide" style={{ color: "var(--pw-text-muted)" }}>{d.toLocaleDateString("en-GB", { month: "short" })}</div>
    </div>
  );
}

function ProjectCard({ project }) {
  const tone = statusTone(project.status);
  const dateRange = formatDateRange(project.shoot_dates_start);
  return (
    <div className="pw-card p-5 transition-colors hover:border-[#DAD5C6]" data-testid={`partner-workspace-project-card-${project.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-bold text-[16px]" style={{ color: "var(--pw-text-primary)" }}>{project.name}</div>
          <div className="flex items-center gap-3 mt-1.5 text-[12.5px]" style={{ color: "var(--pw-text-muted)" }}>
            {dateRange && <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {dateRange}</span>}
            {project.shoot_locations && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {project.shoot_locations}</span>}
          </div>
        </div>
        {project.status && <span className="pw-pill shrink-0" style={TONE_STYLE[tone]}>{project.status}</span>}
      </div>
      <div className="pt-3 border-t flex items-center justify-between" style={{ borderColor: "var(--pw-divider)" }}>
        <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold" style={{ color: "var(--pw-text-secondary)" }}>
          <Users className="h-3.5 w-3.5" /> {project.crew_count} crew logged
        </span>
        {project.category && <span className="text-[12px]" style={{ color: "var(--pw-text-faint)" }}>{project.category}</span>}
      </div>
    </div>
  );
}

function ActionRequiredCard({ item }) {
  return (
    <div className="pw-card p-5" data-testid={`partner-workspace-action-card-${item.id}`}>
      <span className="pw-pill mb-3" style={{ background: "var(--pw-pending-bg)", color: "var(--pw-pending-text)" }}>{item.status || "Awaiting your confirmation"}</span>
      <div className="font-extrabold text-[18px] mb-1.5" style={{ fontFamily: "var(--pw-font-display)" }}>{item.project_name || item.name}</div>
      {(item.proposed_budget !== null && item.proposed_budget !== undefined) && (
        <div className="text-[14px] font-bold mb-3 tabular-nums" style={{ color: "var(--pw-text-primary)" }}>{formatMoney(item.proposed_budget, item.currency)}</div>
      )}
      {item.brief && <p className="text-[13.5px] mb-4" style={{ color: "var(--pw-text-secondary)" }}>{item.brief}</p>}
      <div className="flex items-center gap-2">
        <Link to="/partner-portal/requests" className="pw-btn-primary flex-1 text-center px-4 py-2 text-[13px]" data-testid={`partner-workspace-review-request-${item.id}`}>Review Request →</Link>
        <Link to="/partner-portal/messages" className="pw-btn-secondary px-4 py-2 text-[13px]">Message NJM</Link>
      </div>
    </div>
  );
}

export default function PartnerWorkspaceHome() {
  const { data, triggerUpload } = useOutletContext();
  const [rateCard, setRateCard] = useState(null);

  useEffect(() => {
    partnerWorkspaceApi.rateCard().then((r) => setRateCard(r.data.header)).catch(() => {});
  }, []);

  const partner = data?.partner || {};
  const projects = data?.active_projects || [];
  const upcoming = data?.upcoming_shoots || [];
  const actionRequired = data?.action_required || [];

  return (
    <div className="max-w-[1180px] mx-auto px-8 py-10 pw-enter" data-testid="partner-workspace-home-page">
      {/* 1a. Page header */}
      <div className="flex items-start justify-between flex-wrap gap-5 mb-9">
        <div className="flex items-center gap-4">
          <button onClick={triggerUpload} className="relative shrink-0" data-testid="partner-workspace-home-logo-tile" title="Update company logo">
            {partner.has_logo && partner.id ? (
              <img src={`${partnerWorkspaceApi.logoUrl(partner.id)}${partner._logoNonce ? `?v=${partner._logoNonce}` : ""}`} alt="" className="h-[72px] w-[72px] rounded-2xl object-cover" loading="lazy" decoding="async" />
            ) : (
              <div className="h-[72px] w-[72px] rounded-2xl flex items-center justify-center" style={{ background: "var(--pw-ink)" }}>
                <span className="text-[26px] font-extrabold" style={{ color: "var(--pw-yellow)", fontFamily: "var(--pw-font-display)" }}>
                  {(partner.name || "?").trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?"}
                </span>
              </div>
            )}
            <span className="absolute -bottom-1 -right-1 h-[26px] w-[26px] rounded-full flex items-center justify-center border-2 border-white" style={{ background: "var(--pw-yellow)" }}>
              <Camera className="h-3.5 w-3.5" style={{ color: "var(--pw-ink)" }} />
            </span>
          </button>
          <div>
            <h1 className="pw-display text-[30px]">{greeting()}, {firstName(partner.name)}</h1>
            <p className="text-[14.5px] mt-1" style={{ color: "var(--pw-text-secondary)" }}>
              NJM Production Partner{partner.city || partner.country ? ` · ${[partner.city, partner.country].filter(Boolean).join(", ")}` : ""} · {todayLong()} ·{" "}
              <button onClick={triggerUpload} data-testid="partner-workspace-update-logo-link" className="font-bold" style={{ color: "var(--pw-pending-text)" }}>Update logo</button>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <Link to="/partner-portal/rate-card" className="pw-btn-secondary px-4 py-2 text-[13px]" data-testid="partner-workspace-view-rate-card-btn">View Rate Card</Link>
          <Link to="/partner-portal/requests" className="pw-btn-primary px-4 py-2 text-[13px]" data-testid="partner-workspace-open-requests-btn">Open Requests</Link>
        </div>
      </div>

      {/* 1b. Action Required band */}
      {actionRequired.length > 0 && (
        <div className="rounded-[22px] p-5 mb-9 border" data-testid="partner-workspace-action-required-band"
          style={{ background: "linear-gradient(180deg,#FCF7E3,#FBF9F0)", borderColor: "var(--pw-action-band-border)" }}>
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div className="flex items-center gap-2.5">
              <span className="h-[9px] w-[9px] rounded-full pw-pulse-dot" style={{ background: "var(--pw-yellow)" }} />
              <span className="text-[13px] font-bold uppercase tracking-[0.14em]" style={{ color: "#7A5E12" }}>Action Required</span>
              <span className="pw-pill" style={{ background: "var(--pw-ink)", color: "#fff" }}>{actionRequired.length}</span>
            </div>
            <span className="text-[12.5px]" style={{ color: "var(--pw-text-muted)" }}>Everything NJM needs from you, in one place</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {actionRequired.map((item) => <ActionRequiredCard key={item.id} item={item} />)}
          </div>
        </div>
      )}

      {/* 1c. Main + right rail */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.7fr_1fr] gap-6 items-start">
        <div>
          <div className="flex items-center justify-between mb-4">
            <span className="pw-eyebrow">Active Projects</span>
          </div>
          {projects.length === 0 ? (
            <div className="pw-card p-10 text-center" data-testid="partner-workspace-projects-empty">
              <p className="text-[14.5px]" style={{ color: "var(--pw-text-secondary)" }}>No active projects yet — they'll show up here as soon as NJM assigns one to you.</p>
            </div>
          ) : (
            <div className="space-y-3.5">
              {projects.map((p) => <ProjectCard key={p.id} project={p} />)}
            </div>
          )}
        </div>

        <div className="space-y-4.5">
          {/* Rate Card status — PP2a (2026-02) real status; honest empty state if not yet issued */}
          <div className="pw-card p-5" data-testid="partner-workspace-rate-card-status">
            <div className="flex items-center justify-between mb-3">
              <span className="pw-eyebrow">Rate Card</span>
              <Wallet className="h-4 w-4" style={{ color: "var(--pw-text-faint)" }} />
            </div>
            {rateCard ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <span className="pw-pill" style={{ background: "var(--pw-success-bg)", color: "var(--pw-success-text)" }}>{(rateCard.status || "").toUpperCase()}</span>
                  <span className="text-[12.5px]" style={{ color: "var(--pw-text-muted)" }}>{rateCard.line_items} line items</span>
                </div>
                {rateCard.pending_count > 0 && (
                  <p className="text-[13px] mb-3" style={{ color: "var(--pw-pending-text)" }}>{rateCard.pending_count} proposal{rateCard.pending_count === 1 ? "" : "s"} pending NJM's review</p>
                )}
              </>
            ) : (
              <p className="text-[13.5px] mb-4" style={{ color: "var(--pw-text-secondary)" }}>No rate card issued yet — NJM will share yours here.</p>
            )}
            <Link to="/partner-portal/rate-card" className="pw-btn-secondary w-full text-center block px-4 py-2 text-[13px]">{rateCard ? "View Rate Card" : "Learn more"}</Link>
          </div>

          <div className="pw-card p-5" data-testid="partner-workspace-upcoming-shoots">
            <span className="pw-eyebrow block mb-4">Upcoming Shoots</span>
            {upcoming.length === 0 ? (
              <p className="text-[13.5px]" style={{ color: "var(--pw-text-secondary)" }}>No upcoming shoots scheduled.</p>
            ) : (
              <div className="space-y-3">
                {upcoming.map((s) => (
                  <div key={s.id} className="flex items-center gap-3" data-testid={`partner-workspace-shoot-row-${s.id}`}>
                    <ShootDateTile iso={s.shoot_dates_start} />
                    <div className="min-w-0">
                      <div className="text-[13.5px] font-bold truncate">{s.name}</div>
                      {s.shoot_locations && <div className="text-[12px] truncate" style={{ color: "var(--pw-text-muted)" }}>{s.shoot_locations}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pw-card p-5" data-testid="partner-workspace-messages">
            <div className="flex items-center justify-between mb-4">
              <span className="pw-eyebrow">Messages</span>
              <MessageSquare className="h-4 w-4" style={{ color: "var(--pw-text-faint)" }} />
            </div>
            <p className="text-[13.5px]" style={{ color: "var(--pw-text-secondary)" }}>Messaging is coming in a later phase.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
